import { useState } from "react";
import {
  LayoutGridIcon,
  MessageCircleReplyIcon,
  MessageSquareIcon,
  MessagesSquareIcon,
  QuoteIcon,
  SendIcon,
  WrenchIcon,
} from "lucide-react";

import { Column, EmptyState, ErrorText, LoadingIndicator } from "@/components";
import { ItemCard } from "@/components/tools/assistant/ItemCard";
import {
  formatMinutes,
  minutesBetween,
  relative,
} from "@/components/tools/assistant/format";
import { Chip, Tag, Why } from "@/components/tools/assistant/shared";
import {
  accentFor,
  chatCount,
  DELIVERY,
  IMPORTANT,
  isPushMessage,
  itemStatus,
  MESSAGE_STATUS,
  messageKind,
  ORIGIN,
  pushCount,
  pushTrigger,
  Tone,
} from "@/components/tools/assistant/vocab";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";

import {
  useGetAssistantOverview,
  useGetAssistantThreadMessages,
} from "@/hooks/queries/useGetAssistantAdmin";
import { dates } from "@/lib/dates";
import { cn } from "@/lib/utils";
import {
  AssistantAttachmentRef,
  AssistantMessage,
  AssistantToolCall,
} from "@/types";

export const AssistantConversationsTab = ({
  days,
  threadId,
  onSelectThread,
}: {
  days: number;
  threadId: string | undefined;
  onSelectThread: (threadId: string) => void;
}) => {
  const { data: overview, status } = useGetAssistantOverview({ days });

  if (status === "pending") return <LoadingIndicator variant="medium" />;
  if (status === "error")
    return <ErrorText errorMessage="Error loading assistant threads." />;

  return (
    <div className="flex min-h-0 flex-1 gap-4">
      <Column className="w-[260px] shrink-0 gap-1 overflow-auto border-r pr-3">
        {overview.threads.map((t) => (
          <button
            className={cn(
              "flex flex-col gap-1 rounded-lg px-3 py-2 text-left hover:bg-slate-100",
              t.threadId === threadId && "bg-slate-100",
            )}
            key={t.threadId}
            onClick={() => {
              onSelectThread(t.threadId);
            }}
            type="button"
          >
            <span className="font-medium text-slate-900">{t.staffer.name}</span>
            <span className="text-xs text-slate-500">
              {t.isOwn ? "" : `viewed by ${t.user.name} · `}
              {relative(t.lastMessageAt)}
            </span>
            <span className="flex items-center gap-2 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1">
                <MessageSquareIcon className="size-3" />
                {chatCount(t)} chat
              </span>
              <span className="inline-flex items-center gap-1 text-violet-700">
                <SendIcon className="size-3" />
                {pushCount(t)} push
              </span>
            </span>
            {(t.items.open > 0 || t.failedReplies > 0) && (
              <div className="flex flex-wrap gap-1">
                {t.items.open > 0 && (
                  <Chip
                    count={t.items.open}
                    def={itemStatus({ status: "open", resolvedBy: null })}
                  />
                )}
                {t.failedReplies > 0 && (
                  <Chip count={t.failedReplies} def={MESSAGE_STATUS.FAILED} />
                )}
              </div>
            )}
          </button>
        ))}
      </Column>
      <Column className="min-w-0 flex-1">
        {threadId ? (
          <ThreadMessages threadId={threadId} />
        ) : (
          <EmptyState
            description="Pick a Staffer to read their thread with the assistant."
            Icon={MessagesSquareIcon}
            title="No thread selected"
          />
        )}
      </Column>
    </div>
  );
};

type MessageFilter = "all" | "chat" | "push";

const MESSAGE_FILTERS: { value: MessageFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "chat", label: "Chat only" },
  { value: "push", label: "Push only" },
];

const ThreadMessages = ({ threadId }: { threadId: string }) => {
  const { data, status, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useGetAssistantThreadMessages({ threadId });
  const [filter, setFilter] = useState<MessageFilter>("all");

  if (status === "pending") return <LoadingIndicator variant="medium" />;
  if (status === "error")
    return <ErrorText errorMessage="Error loading messages." />;

  // Pages are newest first; each page is already oldest first.
  const all = [...data.pages].reverse().flatMap((page) => page.messages);
  const pushes = all.filter(isPushMessage).length;
  const messages =
    filter === "all"
      ? all
      : all.filter((m) => isPushMessage(m) === (filter === "push"));

  return (
    <Column className="gap-3 overflow-auto pb-6 pr-2">
      <div className="flex flex-wrap items-center gap-2">
        <ButtonGroup>
          {MESSAGE_FILTERS.map((f) => (
            <Button
              key={f.value}
              onClick={() => {
                setFilter(f.value);
              }}
              size="sm"
              variant={filter === f.value ? "default" : "outline"}
            >
              {f.label}
            </Button>
          ))}
        </ButtonGroup>
        <span className="text-xs text-slate-500">
          {(all.length - pushes).toString()} chat · {pushes.toString()} push
          {hasNextPage && " loaded"}
        </span>
      </div>
      {hasNextPage && (
        <Button
          className="self-center"
          disabled={isFetchingNextPage}
          onClick={() => {
            void fetchNextPage();
          }}
          size="sm"
          variant="outline"
        >
          {isFetchingNextPage ? "Loading…" : "Load older"}
        </Button>
      )}
      {messages.length === 0 && (
        <span className="text-sm text-slate-500">
          {all.length === 0 ? "No messages yet." : "No messages match this filter."}
        </span>
      )}
      {messages.map((m) => (
        <MessageBubble key={m.id} message={m} />
      ))}
    </Column>
  );
};

const MessageBubble = ({ message }: { message: AssistantMessage }) => {
  const isUser = message.role === "USER";
  const meta = message.metadata;
  const kind = messageKind(message);
  const isPush = isPushMessage(message);
  const trigger = isPush && meta?.trigger ? pushTrigger(meta.trigger) : null;
  const origin = meta?.origin;

  return (
    <Column
      className={cn(
        "max-w-[80%] gap-2 rounded-2xl border px-4 py-3",
        isUser ? "self-end bg-blue-50/60" : "self-start bg-white",
        trigger &&
          cn(
            "border-l-4 border-dashed border-violet-300 bg-violet-50/50",
            accentFor(trigger.tone),
          ),
        message.status === "FAILED" && "border-red-300 bg-red-50",
      )}
    >
      <div className="flex flex-wrap items-center gap-1.5">
        {trigger ? (
          <>
            <span
              className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-violet-600 px-2 py-0.5 text-xs font-semibold text-white"
              title={`Phoebe sent this on her own.\n\n${trigger.hint}`}
            >
              <SendIcon className="size-3.5" />
              Push · {trigger.label}
            </span>
            {meta?.important && <Chip def={IMPORTANT} />}
            {meta?.delivery && <Chip def={DELIVERY[meta.delivery]} />}
            {origin && <Chip def={ORIGIN[origin]} />}
          </>
        ) : (
          <Chip def={kind} />
        )}
        {message.status !== "COMPLETE" && (
          <Chip def={MESSAGE_STATUS[message.status]} />
        )}
        <span className="text-xs text-slate-500">
          {dates.formatShortDateTime(message.createdAt)}
        </span>
      </div>

      {/* Why it went out when it did. */}
      {origin === "held_for_chat" && meta?.plannedAt && (
        <Why def={ORIGIN.held_for_chat}>
          Would have gone out {dates.formatShortDateTime(meta.plannedAt)}; held{" "}
          <span className="font-semibold">
            {formatMinutes(minutesBetween(meta.plannedAt, message.createdAt))}
          </span>{" "}
          until the Staffer stopped chatting.
        </Why>
      )}
      {origin === "follow_up" && meta?.why && (
        <Why def={ORIGIN.follow_up}>{meta.why}</Why>
      )}

      {meta?.quote && (
        <div className="flex items-start gap-1.5 border-l-2 border-slate-300 pl-2 text-xs text-slate-500">
          <QuoteIcon className="mt-0.5 size-3 shrink-0" />
          {meta.quote.excerpt}
        </div>
      )}
      <p className="whitespace-pre-wrap text-sm text-slate-800">
        {message.content || <span className="text-slate-400">(empty)</span>}
      </p>
      {meta?.error && (
        <p className="rounded-md bg-red-100 px-2 py-1 text-xs text-red-700">
          {meta.error}
        </p>
      )}
      {meta?.items && meta.items.length > 0 && (
        <Column className="gap-2">
          {meta.items.map((item) => (
            <ItemCard compact item={item} key={item.key} />
          ))}
        </Column>
      )}
      {meta?.attachments && meta.attachments.length > 0 && (
        <div className="flex flex-wrap items-center gap-1 text-xs">
          <LayoutGridIcon className="size-3.5 text-slate-400" />
          {meta.attachments.map((a, i) => (
            <Tag key={`${a.kind}-${i.toString()}`} tone={ATTACHMENT_TONE[a.kind]}>
              {describeAttachment(a)}
            </Tag>
          ))}
        </div>
      )}
      {meta?.suggestedReplies && meta.suggestedReplies.length > 0 && (
        <div className="flex flex-wrap items-center gap-1 text-xs">
          <MessageCircleReplyIcon className="size-3.5 text-slate-400" />
          {meta.suggestedReplies.map((s) => (
            <span
              className="rounded-full border border-slate-300 px-2 py-0.5 text-slate-700"
              key={s}
            >
              {s}
            </span>
          ))}
        </div>
      )}
      {meta?.toolCalls && meta.toolCalls.length > 0 && (
        <Column className="gap-1">
          {meta.toolCalls.map((call, i) => (
            <ToolCallRow call={call} key={`${call.name}-${i.toString()}`} />
          ))}
        </Column>
      )}
    </Column>
  );
};

const ATTACHMENT_TONE: Record<AssistantAttachmentRef["kind"], Tone> = {
  candidate: "blue",
  job: "indigo",
  queue: "violet",
  interview: "teal",
  reminder: "amber",
};

const describeAttachment = (a: AssistantAttachmentRef): string => {
  switch (a.kind) {
    case "candidate":
      return `Candidate ${a.candidacyId.slice(0, 8)}`;
    case "job":
      return `Job ${a.jobId.slice(0, 8)}`;
    case "queue":
      return `List "${a.title}" · ${a.candidacyIds.length.toString()}`;
    case "interview":
      return `Interview #${a.interviewId.toString()}`;
    case "reminder":
      return "Reminder";
  }
};

// Tool outputs that mean the call did not get its data.
const FAILED_OUTPUT = /^(Query failed|Unknown jobId|\{"error")/;

/** One tool call: name and timing, expanding to its arguments and output. */
const ToolCallRow = ({ call }: { call: AssistantToolCall }) => {
  const failed = FAILED_OUTPUT.test(call.outputPreview ?? "");
  const sql = typeof call.args?.sql === "string" ? call.args.sql : null;
  const otherArgs = Object.entries(call.args ?? {}).filter(
    ([key]) => key !== "sql",
  );
  const hasDetail = !!sql || otherArgs.length > 0 || !!call.outputPreview;

  const summary = (
    <span className="inline-flex flex-wrap items-center gap-1">
      <WrenchIcon className="size-3.5 text-slate-400" />
      <Tag tone={failed ? "red" : undefined}>
        {call.name} · {call.durationMs}ms
        {call.outputChars !== undefined &&
          ` · ${call.outputChars.toLocaleString()} chars`}
        {failed && " · failed"}
      </Tag>
    </span>
  );
  if (!hasDetail) return summary;

  return (
    <details className="group text-xs">
      <summary className="cursor-pointer list-none">{summary}</summary>
      <Column className="mt-1 gap-1.5 rounded-md border bg-slate-50 p-2">
        {sql && (
          <pre className="overflow-x-auto whitespace-pre-wrap font-mono text-[11px] text-slate-800">
            {sql}
          </pre>
        )}
        {otherArgs.length > 0 && (
          <pre className="overflow-x-auto whitespace-pre-wrap font-mono text-[11px] text-slate-700">
            {JSON.stringify(Object.fromEntries(otherArgs), null, 2)}
          </pre>
        )}
        {call.outputPreview && (
          <div className="flex flex-col gap-0.5">
            <span className="font-medium text-slate-500">Returned</span>
            <pre
              className={cn(
                "overflow-x-auto whitespace-pre-wrap font-mono text-[11px]",
                failed ? "text-red-700" : "text-slate-600",
              )}
            >
              {call.outputPreview}
              {call.outputChars !== undefined &&
                call.outputChars > call.outputPreview.length &&
                " …"}
            </pre>
          </div>
        )}
      </Column>
    </details>
  );
};
